import { useEffect, useMemo, useRef, useState } from 'react';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { InputRef } from 'antd';
import { icdCodeConstants, isTest } from '@app/constants';
import i18n from '@app/i18n';
import { useDebounce } from '@app/utils';
import { diseasesKeys } from '@data/constants';
import {
  DiseasesApiGetDiseasesV2Request,
  DiseaseV2ViewChild,
  ListDiseaseV2View,
} from '@data/sdk/match';
import { MatchingApi } from '@data/utils/axios-instance';
import { Language } from '@data/utils/enums';
import { APIError, DiseaseSearchParams } from '@data/utils/types';
import { DiseaseHierarchyOptionV2, DiseaseOptionV2 } from '..';
import { useDiseaseSearchAnalytics } from './useAnalytics';

const MINIMUM_SEARCH_LENGTH = 3;

type DraftTagType = 'parent' | 'child';

type DraftTagOption = {
  children?: DiseaseV2ViewChild[];
  icdCode: string;
  name: string;
  parentIcdCode?: string;
  parentName?: string;
  type: DraftTagType;
};

export const useDiseasesSearch = (
  value: DiseaseOptionV2[],
  onChange: (diseases: DiseaseOptionV2[]) => void,
  autoFocus?: boolean,
  allowHealthy?: boolean,
  enabledAnalytics?: boolean,
  prefetchedHierarchy: DiseaseHierarchyOptionV2[] = [],
) => {
  const inputRef = useRef<InputRef>(null);
  const [isDiseasesSearchOpen, setIsDiseasesSearchOpen] = useState(false);
  const [draftSelectedOptions, setDraftSelectedOptions] = useState<DiseaseOptionV2[]>(value);

  const openDiseasesSearch = () => {
    setDraftSelectedOptions(value);
    setIsDiseasesSearchOpen(true);
  };

  const closeDiseasesSearchWithoutApply = () => {
    setDraftSelectedOptions(value);
    setIsDiseasesSearchOpen(false);
  };

  const applyDiseasesSelection = () => {
    onChange(draftSelectedOptions);
    setIsDiseasesSearchOpen(false);
  };

  const handleRemoveDiseaseImmediately = (icdCode: string) => {
    const updatedSelection = draftSelectedOptions.filter((d) => d.icdCode !== icdCode);
    setDraftSelectedOptions(updatedSelection);
    onChange(updatedSelection);
  };

  // Used for actual state
  const isDiseaseSelected = (icdCode: string) => {
    return draftSelectedOptions.some((d) => d.icdCode === icdCode);
  };

  // Used for ui checkboxes
  const isDiseaseChecked = (icdCode: string, parentIcdCode?: string) => {
    if (parentIcdCode) {
      return isDiseaseSelected(icdCode) || isDiseaseSelected(parentIcdCode);
    }
    return isDiseaseSelected(icdCode);
  };

  const isParentIndeterminate = (parentIcdCode: string, children: DiseaseV2ViewChild[]) => {
    if (isDiseaseSelected(parentIcdCode)) {
      return false;
    }

    return children.some((child) => isDiseaseSelected(child.icdCode));
  };

  const handleSelectDisease = (
    icdCode: string,
    name: string,
    parentIcdCode?: string,
    parentName?: string,
    children?: DiseaseV2ViewChild[],
  ) => {
    if (parentIcdCode && children) {
      // level 3 logic (G31.0)
      // should be included in state only if was explicitly selected
      const selected = isDiseaseChecked(icdCode, parentIcdCode);

      if (selected) {
        // if parent was selected deselect it and child but include other children
        const isParentSelected = isDiseaseSelected(parentIcdCode);
        if (isParentSelected) {
          const childrenToInclude = children
            ?.filter((c) => c.icdCode !== icdCode)
            .map((c) => ({ icdCode: c.icdCode, name: c.name }));

          setDraftSelectedOptions([
            ...draftSelectedOptions.filter((d) => d.icdCode !== parentIcdCode),
            ...childrenToInclude,
          ]);
        } else {
          setDraftSelectedOptions(draftSelectedOptions.filter((d) => d.icdCode !== icdCode));
        }
      } else {
        const selectedChildren = draftSelectedOptions.filter((d) =>
          d.icdCode.startsWith(`${parentIcdCode}.`),
        );

        // if all children are selected, they should be excluded and parent should be included
        setDraftSelectedOptions(
          selectedChildren.length === (children?.length || 0) - 1
            ? [
                ...draftSelectedOptions.filter(
                  (d) => !selectedChildren.some((sc) => sc.icdCode === d.icdCode),
                ),
                { icdCode: parentIcdCode, name: parentName || '' },
              ]
            : [...draftSelectedOptions, { icdCode, name }],
        );
      }
    } else {
      // level 2 logic (G31) only check exact match
      const selected = isDiseaseSelected(icdCode);

      if (selected) {
        setDraftSelectedOptions(draftSelectedOptions.filter((d) => d.icdCode !== icdCode));
      } else {
        // if parent is selected, all children should be excluded
        setDraftSelectedOptions([
          ...draftSelectedOptions.filter((d) => !d.icdCode.startsWith(`${icdCode}.`)),
          { icdCode, name },
        ]);
      }
    }
  };

  const handleRemoveDraftTag = (tag: DraftTagOption) => {
    if (tag.type === 'parent') {
      const updatedSelection = draftSelectedOptions.filter(
        (d) => d.icdCode !== tag.icdCode && !d.icdCode.startsWith(`${tag.icdCode}.`),
      );
      setDraftSelectedOptions(updatedSelection);
      return;
    }

    if (tag.parentIcdCode && tag.parentName && tag.children) {
      // Mirror checkbox behavior: removing a child chip should "toggle" the tri-state
      // under the parent group.
      handleSelectDisease(tag.icdCode, tag.name, tag.parentIcdCode, tag.parentName, tag.children);
      return;
    }

    // If we don't know the parent group, fall back to removing only the chip itself.
    const updatedSelection = draftSelectedOptions.filter((d) => d.icdCode !== tag.icdCode);
    setDraftSelectedOptions(updatedSelection);
  };

  const handleRemoveSelectedTagImmediately = (tag: DraftTagOption) => {
    if (tag.type === 'parent') {
      onChange(
        value.filter((d) => d.icdCode !== tag.icdCode && !d.icdCode.startsWith(`${tag.icdCode}.`)),
      );
      return;
    }

    if (tag.parentIcdCode && tag.parentName && tag.children) {
      const isParentSelected = value.some((d) => d.icdCode === tag.parentIcdCode);

      if (isParentSelected) {
        const childrenToInclude = tag.children
          .filter((c) => c.icdCode !== tag.icdCode)
          .map((c) => ({ icdCode: c.icdCode, name: c.name || c.icdCode }));

        onChange([...value.filter((d) => d.icdCode !== tag.parentIcdCode), ...childrenToInclude]);
        return;
      }

      onChange(value.filter((d) => d.icdCode !== tag.icdCode));
      return;
    }

    onChange(value.filter((d) => d.icdCode !== tag.icdCode));
  };

  const [query, setQuery] = useState<DiseaseSearchParams>({
    limit: isTest ? 100 : 10,
    offset: 0,
    searchString: undefined,
    language: i18n.language as Language,
  });

  const debouncedSearch = useDebounce(query.searchString);

  const queryEnabled =
    (!!debouncedSearch && debouncedSearch.trim().length >= MINIMUM_SEARCH_LENGTH) || isTest;

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching } = useInfiniteQuery<
    ListDiseaseV2View,
    APIError
  >({
    queryKey: diseasesKeys.infiniteList({
      ...query,
      searchString: debouncedSearch,
      language: i18n.language as Language,
    }),
    initialPageParam: {
      ...query,
      searchString: debouncedSearch,
      language: i18n.language as Language,
    },
    queryFn: async ({ pageParam }) => {
      const response = await MatchingApi.diseases.getDiseasesV2(
        pageParam as DiseasesApiGetDiseasesV2Request,
      );
      return response.data;
    },
    enabled: queryEnabled,
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

      return nextOffset < lastPage.count
        ? {
            limit: query.limit,
            offset: nextOffset,
            searchString: debouncedSearch || undefined,
            language: i18n.language as Language,
          }
        : undefined;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
  });

  const diseases = data?.pages.flatMap((page) => page.items || []) || [];

  const handleSearchChange = (v: string) => {
    setQuery({ ...query, searchString: v || undefined, offset: 0 });
  };

  const showEmptyState = ((queryEnabled && !diseases.length) || !queryEnabled) && !isLoading;

  const hasDraftChanges = useMemo(() => {
    const appliedIcdCodes = new Set(value.map((d) => d.icdCode));
    const draftIcdCodes = new Set(draftSelectedOptions.map((d) => d.icdCode));

    if (appliedIcdCodes.size !== draftIcdCodes.size) {
      return true;
    }

    return !Array.from(draftIcdCodes).every((icdCode) => appliedIcdCodes.has(icdCode));
  }, [value, draftSelectedOptions]);

  useEffect(() => {
    const focusInput = () => {
      if (autoFocus && inputRef.current) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    };

    if (isDiseasesSearchOpen) {
      focusInput();
    }
  }, [autoFocus, isDiseasesSearchOpen]);

  const {
    trackDiseaseSearchPopupOpened,
    trackDiseaseSearchDiseaseSelected,
    trackDiseaseSearchDiseaseNotFound,
  } = useDiseaseSearchAnalytics(!!enabledAnalytics);

  useEffect(() => {
    if (isDiseasesSearchOpen) {
      trackDiseaseSearchPopupOpened();
    }
  }, [isDiseasesSearchOpen]);

  useEffect(() => {
    if (!isFetching && !isLoading && debouncedSearch && diseases.length === 0) {
      trackDiseaseSearchDiseaseNotFound();
    }
  }, [debouncedSearch, diseases.length, isLoading, isFetching]);

  useEffect(() => {
    if (enabledAnalytics && value.length > 0) {
      trackDiseaseSearchDiseaseSelected();
    }
  }, [enabledAnalytics, value]);

  const filteredDiseases = (
    allowHealthy ? diseases : diseases.filter((d) => d.icdCode !== icdCodeConstants.HEALTHY)
  ).map((disease) => ({
    ...disease,
    names: disease.names?.filter((altName) => altName !== disease.name) || [],
  }));

  const mergedDiseasesHierarchy = useMemo(() => {
    const diseaseByIcdCode = new Map<string, DiseaseHierarchyOptionV2>();

    prefetchedHierarchy.forEach((disease) => {
      diseaseByIcdCode.set(disease.icdCode, disease);
    });

    filteredDiseases.forEach((disease) => {
      diseaseByIcdCode.set(disease.icdCode, disease);
    });

    return Array.from(diseaseByIcdCode.values());
  }, [filteredDiseases, prefetchedHierarchy]);

  const buildTagOptions = (
    selectedOptions: DiseaseOptionV2[],
    diseasesHierarchy: Array<{
      children?: DiseaseV2ViewChild[];
      icdCode: string;
      name?: string;
    }>,
  ) => {
    const selectedIcdCodes = new Set(selectedOptions.map((d) => d.icdCode));
    const coveredExplicitIcdCodes = new Set<string>();
    const loadedParentCodes = new Set(diseasesHierarchy.map((d) => d.icdCode));

    const result: DraftTagOption[] = [];

    diseasesHierarchy.forEach((disease) => {
      const parentIcdCode = disease.icdCode;
      const parentName = disease.name || disease.icdCode;
      const children = disease.children || [];

      const parentSelected = selectedIcdCodes.has(parentIcdCode);
      const selectedChildren = children.filter((child) => selectedIcdCodes.has(child.icdCode));

      if (parentSelected) {
        result.push({ icdCode: parentIcdCode, name: parentName, type: 'parent' });

        children.forEach((child) => {
          result.push({
            children,
            icdCode: child.icdCode,
            name: child.name || child.icdCode,
            parentIcdCode,
            parentName,
            type: 'child',
          });

          if (selectedIcdCodes.has(child.icdCode)) {
            coveredExplicitIcdCodes.add(child.icdCode);
          }
        });

        coveredExplicitIcdCodes.add(parentIcdCode);
        return;
      }

      if (selectedChildren.length === 0) {
        return;
      }

      result.push({ icdCode: parentIcdCode, name: parentName, type: 'parent' });

      selectedChildren.forEach((child) => {
        result.push({
          children,
          icdCode: child.icdCode,
          name: child.name || child.icdCode,
          parentIcdCode,
          parentName,
          type: 'child',
        });
        coveredExplicitIcdCodes.add(child.icdCode);
      });
    });

    // Keep explicitly selected items even if their hierarchy isn't currently loaded.
    selectedOptions.forEach((selected) => {
      if (coveredExplicitIcdCodes.has(selected.icdCode)) {
        return;
      }

      const isLikelyParent =
        loadedParentCodes.has(selected.icdCode) || !selected.icdCode.includes('.');
      const type: DraftTagType = isLikelyParent ? 'parent' : 'child';

      result.push({
        icdCode: selected.icdCode,
        name: selected.name,
        type,
      });
    });

    return result;
  };

  const selectedTagOptions = useMemo(
    () => buildTagOptions(value, mergedDiseasesHierarchy),
    [value, mergedDiseasesHierarchy],
  );

  const draftTagOptions = useMemo(
    () => buildTagOptions(draftSelectedOptions, mergedDiseasesHierarchy),
    [draftSelectedOptions, mergedDiseasesHierarchy],
  );

  return {
    inputRef,
    diseases: filteredDiseases,
    hasNextPage,
    queryEnabled,
    showEmptyState,
    hasDraftChanges,
    selectedOptions: value,
    selectedTagOptions,
    draftSelectedOptions,
    draftTagOptions,
    isDiseasesSearchOpen,
    count: diseases.length,
    searchString: query.searchString,
    isInitialLoading: queryEnabled && isLoading,
    isFetchingMore: queryEnabled && isFetching && !isLoading,
    fetchNextPage,
    isDiseaseSelected,
    isDiseaseChecked,
    isParentIndeterminate,
    handleSelectDisease,
    handleRemoveDraftTag,
    handleSearchChange,
    openDiseasesSearch,
    closeDiseasesSearchWithoutApply,
    applyDiseasesSelection,
    handleRemoveDiseaseImmediately,
    handleRemoveSelectedTagImmediately,
  };
};
